import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Briefcase, MapPin, Clock, Paperclip, Send, CheckCircle2 } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { formatDate } from "../lib/constants";
import { toast } from "sonner";

export default function VagaDetail() {
  const { id } = useParams();
  const [job, setJob] = useState(null);
  const [err, setErr] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", phone: "", is_cooperado: "no", community: "", message: "" });
  const [cv, setCv] = useState(null);
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    setJob(null); setErr(null);
    api.get(`/jobs/${id}`).then((r) => setJob(r.data)).catch(() => setErr("Vaga não encontrada ou encerrada."));
  }, [id]);

  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      if (cv) fd.append("cv", cv);
      await api.post(`/jobs/${id}/apply`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setDone(true);
      toast.success("Candidatura enviada!");
    } catch (e2) {
      toast.error(formatApiError(e2));
    } finally {
      setSending(false);
    }
  };

  if (err)
    return (
      <div className="container-page py-24" data-testid="vaga-not-found">
        <p className="text-forest-900/70">{err}</p>
        <Link to="/vagas" className="btn-outline mt-6 inline-flex">Voltar para vagas</Link>
      </div>
    );

  if (!job) return <div className="container-page py-24 text-forest-900/60">Carregando...</div>;

  return (
    <div data-testid="vaga-detail-page" className="py-16">
      <div className="container-page max-w-5xl">
        <Link to="/vagas" className="inline-flex items-center gap-2 text-sm text-forest-700 hover:text-copaiba-700 mb-8">
          <ArrowLeft className="w-4 h-4" /> Todas as vagas
        </Link>

        <header className="mb-10">
          <div className="eyebrow mb-3">Vaga aberta · {formatDate(job.created_at)}</div>
          <h1 className="font-display text-4xl md:text-5xl text-forest-900 leading-tight">{job.title}</h1>
          <div className="flex flex-wrap gap-x-6 gap-y-2 mt-5 text-sm text-forest-900/75">
            {job.community && <span className="inline-flex items-center gap-1.5"><MapPin className="w-4 h-4" /> {job.community}</span>}
            {job.contract_type && <span className="inline-flex items-center gap-1.5"><Briefcase className="w-4 h-4" /> {job.contract_type}</span>}
            {job.deadline && <span className="inline-flex items-center gap-1.5"><Clock className="w-4 h-4" /> Inscrições até {job.deadline}</span>}
          </div>
        </header>

        <article className="prose prose-lg max-w-none text-forest-900/90 whitespace-pre-wrap mb-12">
          {job.description}
        </article>

        {done ? (
          <div data-testid="apply-success" className="bg-white border border-cream-400 p-10 text-center">
            <CheckCircle2 className="w-12 h-12 text-forest-700 mx-auto mb-4" />
            <h2 className="font-display text-3xl text-forest-900">Candidatura recebida!</h2>
            <p className="text-forest-900/80 mt-3">A secretaria da cooperativa vai analisar e entrar em contato pelo e-mail informado.</p>
            <Link to="/vagas" className="btn-outline mt-6 inline-flex">Ver outras vagas</Link>
          </div>
        ) : (
          <form onSubmit={submit} className="bg-white border border-cream-400 p-8 md:p-10" data-testid="apply-form">
            <h2 className="font-display text-2xl text-forest-900 mb-6">Candidatar-se a esta vaga</h2>
            <div className="grid md:grid-cols-2 gap-5">
              <Field label="Nome completo" name="name" value={form.name} onChange={onChange} required />
              <Field label="E-mail" name="email" type="email" value={form.email} onChange={onChange} required />
              <Field label="Telefone" name="phone" value={form.phone} onChange={onChange} />
              <Field label="Comunidade onde mora" name="community" value={form.community} onChange={onChange} />
            </div>
            <fieldset className="mt-5">
              <legend className="text-xs uppercase tracking-[0.18em] text-forest-900/70 font-medium mb-2">Você é cooperado(a) da COOMFLONA?</legend>
              <div className="flex gap-6 text-sm">
                <label className="inline-flex items-center gap-2">
                  <input type="radio" name="is_cooperado" value="yes" checked={form.is_cooperado === "yes"} onChange={onChange} data-testid="cooperado-yes" />
                  Sim, sou cooperado(a)
                </label>
                <label className="inline-flex items-center gap-2">
                  <input type="radio" name="is_cooperado" value="no" checked={form.is_cooperado === "no"} onChange={onChange} data-testid="cooperado-no" />
                  Não sou
                </label>
              </div>
            </fieldset>

            <label className="block mt-5">
              <span className="text-xs uppercase tracking-[0.18em] text-forest-900/70 font-medium">Mensagem / Carta de apresentação (opcional)</span>
              <textarea
                name="message"
                rows={5}
                value={form.message}
                onChange={onChange}
                data-testid="apply-message"
                className="mt-2 w-full bg-cream-100 border border-cream-400 focus:border-forest-700 focus:outline-none p-3.5 resize-none"
              />
            </label>

            <label className="mt-5 flex items-center gap-3 px-4 py-3 border border-dashed border-cream-400 bg-cream-100 cursor-pointer hover:bg-cream-200">
              <Paperclip className="w-4 h-4 text-forest-700" />
              <span className="text-sm text-forest-900/80">{cv ? cv.name : "Anexar currículo (PDF, DOC, JPG — até 10 MB) · opcional"}</span>
              <input
                type="file"
                accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,application/pdf,image/*"
                onChange={(e) => setCv(e.target.files?.[0] || null)}
                data-testid="cv-input"
                className="hidden"
              />
            </label>

            <button type="submit" disabled={sending} data-testid="apply-submit" className="btn-primary mt-7">
              {sending ? "Enviando..." : (<>Enviar candidatura <Send className="w-4 h-4" /></>)}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

function Field({ label, name, value, onChange, type = "text", required = false }) {
  return (
    <label className="block">
      <span className="text-xs uppercase tracking-[0.18em] text-forest-900/70 font-medium">{label}</span>
      <input
        type={type}
        name={name}
        required={required}
        value={value}
        onChange={onChange}
        data-testid={`apply-${name}`}
        className="mt-2 w-full bg-cream-100 border border-cream-400 focus:border-forest-700 focus:outline-none p-3.5"
      />
    </label>
  );
}
